import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiService } from '../../../core/services/api.service';
import { environment } from '../../../../environments/environment';

export interface StudentListState {
  assessmentId: number;
  classId: number | null;
  /** Which interval the cached list was fetched for, if any */
  intervalId?: number | null;
  students: any[];
  currentPage: number;
  totalPages: number;
  scrollY: number;
  lastSelectedStudentId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class TeacherAssessmentService {
  private apiService = inject(ApiService);
  private studentListState: StudentListState | null = null;

  saveStudentListState(state: StudentListState) {
    this.studentListState = state;
  }

  getStudentListState(): StudentListState | null {
    return this.studentListState;
  }

  clearStudentListState() {
    this.studentListState = null;
  }

  getAssessmentsBySchool(schoolId: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/school/${schoolId}`
    );
  }

  completeStudentInterval(payload: {
    assignment_id?: number | null;
    interval_id?: number | null;
    user_id?: number | null;
    assessment_id?: number | null;
  }): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/programs/assessments/teacher/intervals/complete-student`,
      payload
    );
  }

  getAssessmentDetails(assessmentId: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher/assessments/${assessmentId}`
    );
  }

  getAssessmentSections(
    assessmentId: number,
    parentLayerId?: number | null,
    attemptId?: number | null
  ): Observable<any> {
    let url = `api/v1/teachers/multiple-assessments/${assessmentId}/sections`;
    let params: Record<string, any> = {};

    if (parentLayerId) {
      params['parent_section_id'] = parentLayerId.toString();
    }

    if (attemptId) {
      params['attempt_id'] = attemptId.toString();
    }
    return this.apiService.get<any>(url, undefined, params);
  }

  getSectionQuestions(
    sectionId: number,
    qTypeOverride?: string | null,
    attemptId?: number | null,
    sectionItemId?: number | null
  ): Observable<any> {
    let url = `api/v1/teachers/multiple-assessments/section/${sectionId}/items`;
    let params: Record<string, any> = {};
    console.log('qType', qTypeOverride);
    if (qTypeOverride) {
      params['qType'] = qTypeOverride;
    }
    if (attemptId) {
      params['attemptId'] = attemptId.toString();
    }
    if (sectionItemId) {
      params['sectionItemId'] = sectionItemId.toString();
    }
    console.log(params);
    return this.apiService.get<any>(url, undefined, params);
  }

  getSectionItemOptions(
    sectionItemId: number,
    attemptId?: number | null
  ): Observable<any> {
    let url = `api/v1/teachers/multiple-assessments/section-item/${sectionItemId}/options`;
    let params: Record<string, any> = {};
    if (attemptId) {
      params['attemptId'] = attemptId.toString();
    }
    return this.apiService.get<any>(url, undefined, params);
  }

  getDetailedResults(attempt_id: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/results/${attempt_id}`
    );
  }

  getSectionDetailedResults(
    attempt_id: number,
    section_id: number
  ): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/results/${attempt_id}/section/${section_id}`
    );
  }

  getBmcDetailedResults(attempt_id: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/bmc-results/${attempt_id}`
    );
  }

  getBmcLayer0Results(attempt_id: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/bmc-results/${attempt_id}/layers`
    );
  }

  getBmcLayer1Results(attempt_id: number, section_id: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/bmc-results/${attempt_id}/layer/${section_id}`
    );
  }

  getBmcSectionQuestions(
    attempt_id: number,
    section_id: number
  ): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/bmc-results/${attempt_id}/section/${section_id}/questions`
    );
  }

  saveMultiAiAnalysis(
    attempt_id: number,
    payload: {
      section_id?: number | null;
      section_item_id?: number | null;
      student_id: number;
      assessment_id: number;
      class_id?: number;
      domain_name?: string;
      summary: string;
      area_wise_analysis: any;
      language?: string;
    }
  ): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/teachers/multiple-assessments/bmc-results/${attempt_id}/ai-analysis`,
      payload
    );
  }

  getMultiAiAnalysis(
    attempt_id: number,
    section_id?: number | null,
    section_item_id?: number | null
  ): Observable<any> {
    const params: Record<string, any> = {};
    if (section_id != null) params['section_id'] = section_id.toString();
    if (section_item_id != null)
      params['section_item_id'] = section_item_id.toString();
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/bmc-results/${attempt_id}/ai-analysis`,
      undefined,
      params
    );
  }

  getAssignedStudents(
    assessmentId: number,
    page: number = 1,
    limit: number = 20,
    classId: number | null = null,
    studentId: number | null = null,
    intervalId: number | null = null
  ): Observable<any> {
    const params: any = { page: page.toString(), limit: limit.toString() };
    if (classId) {
      params['classId'] = classId.toString();
    }

    if (studentId) {
      params['studentId'] = studentId.toString();
    }

    // Pins interval resolution so a chosen past interval loads its own attempts
    if (intervalId) {
      params['interval_id'] = intervalId.toString();
    }

    return this.apiService.get<any>(
      `api/v1/programs/assessments/students/${assessmentId}`,
      undefined,
      params
    );
  }

  saveResponse(payload: any): Observable<any> {
    return this.apiService
      .post<
        any,
        any
      >(`api/v1/programs/assessments/teacher-assessment/save-response`, payload)
      .pipe(tap(() => this.markStudentListStale(true)));
  }

  /** Sam5 list-view: paginated LOs under a domain for one student. */
  getListViewItems(params: {
    assessment_id: number;
    domain_section_id: number;
    assignment_id: number;
    student_id: number;
    attempt_id?: number | null;
    interval_id?: number | null;
    page?: number;
    limit?: number;
    q?: string | null;
  }): Observable<any> {
    const query: Record<string, any> = {
      assessment_id: params.assessment_id.toString(),
      domain_section_id: params.domain_section_id.toString(),
      assignment_id: params.assignment_id.toString(),
      student_id: params.student_id.toString(),
      page: (params.page ?? 0).toString(),
      limit: (params.limit ?? 5).toString(),
    };
    if (params.attempt_id) query['attempt_id'] = params.attempt_id.toString();
    if (params.interval_id)
      query['interval_id'] = params.interval_id.toString();
    if (params.q?.trim()) query['q'] = params.q.trim();
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher-assessment/list-view/items`,
      undefined,
      query
    );
  }

  /** Sam5 bulk: domain list with class progress. */
  getBulkDomains(assessmentId: number, classId: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher-assessment/bulk/domains`,
      undefined,
      {
        assessment_id: assessmentId.toString(),
        class_id: classId.toString(),
      }
    );
  }

  /** Sam5 bulk: paginated LOs + per-student assessments. */
  getBulkItems(params: {
    assessment_id: number;
    section_id: number;
    class_id: number;
    page?: number;
    limit?: number;
    interval_id?: number | null;
    q?: string | null;
  }): Observable<any> {
    const query: Record<string, string> = {
      assessment_id: params.assessment_id.toString(),
      section_id: params.section_id.toString(),
      class_id: params.class_id.toString(),
      page: (params.page ?? 0).toString(),
      limit: (params.limit ?? 5).toString(),
    };
    if (params.interval_id != null) {
      query['interval_id'] = params.interval_id.toString();
    }
    if (params.q?.trim()) {
      query['q'] = params.q.trim();
    }
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher-assessment/bulk/items`,
      undefined,
      query
    );
  }

  getLoSearchRelatedDomains(params: {
    assessment_id: number;
    q: string;
    exclude_section_id: number;
    class_id?: number | null;
  }): Observable<any> {
    const query: Record<string, string> = {
      assessment_id: params.assessment_id.toString(),
      q: params.q.trim(),
      exclude_section_id: params.exclude_section_id.toString(),
    };
    if (params.class_id != null) {
      query['class_id'] = params.class_id.toString();
    }
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher-assessment/lo-search/related-domains`,
      undefined,
      query
    );
  }

  /** Sam5 bulk select-all: one LO × many students. */
  saveBulkByItem(payload: {
    assessment_id: number;
    class_id: number;
    question_id: number;
    section_item_id: number;
    responses: any[];
  }): Observable<any> {
    return this.apiService
      .post<
        any,
        any
      >(`api/v1/programs/assessments/teacher-assessment/bulk/save-by-item`, payload)
      .pipe(tap(() => this.markStudentListStale(true)));
  }

  saveBulkResponses(payload: any): Observable<any> {
    return this.apiService
      .post<
        any,
        any
      >(`api/v1/programs/assessments/teacher-assessment/save-bulk-responses`, payload)
      .pipe(tap(() => this.markStudentListStale(true)));
  }

  private studentListStale = false;

  markStudentListStale(stale: boolean) {
    this.studentListStale = stale;
  }

  isStudentListStale(): boolean {
    return this.studentListStale;
  }

  saveObservationsBulk(payload: any): Observable<any> {
    return this.apiService
      .post<
        any,
        any
      >(`api/v1/programs/assessments/teacher-assessment/save-observations-bulk`, payload)
      .pipe(tap(() => this.markStudentListStale(true)));
  }

  uploadMediaEntry(payload: any): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/teachers/multiple-assessments/media/upload`,
      payload
    );
  }

  uploadPhotoEntry(payload: any): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/teachers/multiple-assessments/media/upload-photo`,
      payload
    );
  }

  uploadVideoEntry(payload: any): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/teachers/multiple-assessments/media/upload-video`,
      payload
    );
  }

  linkGalleryMedia(payload: {
    student_id: number;
    attempt_id: number;
    section_item_id: number;
    media_ids: number[];
    assessment_id?: number | null;
    question_id?: number | null;
    response_id?: number | null;
  }): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/teachers/multiple-assessments/media/link-from-gallery`,
      payload
    );
  }

  getStudentMedia(studentId: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/media/student/${studentId}`
    );
  }

  getResponseMedia(responseId: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/media/response/${responseId}`
    );
  }

  deleteMediaEntry(mediaId: number): Observable<any> {
    return this.apiService.delete<any>(
      `api/v1/teachers/multiple-assessments/media/${mediaId}`
    );
  }

  getMediaPresignedUrls(
    studentId: number,
    files: Array<{ fileName: string; mediaType: string }>
  ): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/teachers/multiple-assessments/media/presigned`,
      { student_id: studentId, files }
    );
  }

  getMediaCount(studentId: number, responseId: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/media/count/${studentId}/${responseId}`
    );
  }

  getActiveInterval(
    assessmentId: number,
    assignmentId: number
  ): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher-assessment/active-interval/${assessmentId}/${assignmentId}`
    );
  }

  getSchoolConfigs(schoolId: number = 0): Observable<any> {
    if (!schoolId) {
      schoolId = Number(localStorage.getItem('school-id'));
    }
    const params: Record<string, any> = {};
    if (schoolId != null) params['schoolId'] = schoolId.toString();
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher-assessment/school-config`,
      undefined,
      params
    );
  }

  getSharingState(
    assignmentId: number,
    intervalId?: number | null
  ): Observable<any> {
    const params: Record<string, any> = {};
    if (intervalId != null) params['interval_id'] = intervalId.toString();
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/sharing-state/${assignmentId}`,
      undefined,
      params
    );
  }

  /**
   * Multi-assessment progress tracker (same contract as mobile parent API).
   * level: domain | curriculum_goal | competency
   */
  getProgressTracker(
    assignmentId: number,
    options: {
      schoolId?: number | null;
      level?: 'domain' | 'curriculum_goal' | 'competency';
      domainId?: number | null;
      cgId?: number | null;
    } = {}
  ): Observable<any> {
    const params: Record<string, any> = {
      level: options.level || 'domain',
    };
    if (options.schoolId != null)
      params['school_id'] = options.schoolId.toString();
    if (options.domainId != null)
      params['domain_id'] = options.domainId.toString();
    if (options.cgId != null) params['cg_id'] = options.cgId.toString();
    return this.apiService.get<any>(
      `api/v1/teachers/multiple-assessments/progress-tracker/${assignmentId}`,
      undefined,
      params
    );
  }

  startInterval(assignmentId: number, intervalId: number): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/programs/assessments/teacher/intervals/start`,
      { assignmentId: assignmentId, intervalId: intervalId }
    );
  }

  /** Every interval of an assessment, rolled up for a single class */
  getClassIntervals(
    assessmentId: number,
    classId: number | null = null,
    limit?: number | null
  ): Observable<any> {
    const params: any = {};
    if (classId) {
      params['class_id'] = classId.toString();
    }
    if (limit !== undefined && limit !== null) {
      params['limit'] = limit.toString();
    }
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher/intervals/list/${assessmentId}`,
      undefined,
      params
    );
  }

  /**
   * Advance to the next interval. An empty `students` roster with a `class_id`
   * advances the whole class; naming students advances only them and leaves the
   * rest of the class where it is. The backend resolves which interval comes
   * next from each assignment's own position, so no interval id is needed.
   */
  forceNextIntervals(payload: {
    students: { assignment_id: number }[];
    assessment_id: number | null;
    class_id?: number | null;
    scheduled_date?: number | string | null;
    due_within_days?: number | null;
  }): Observable<any> {
    return this.apiService.post<any, any>(
      `api/v1/programs/assessments/teacher/intervals/force-next`,
      payload
    );
  }

  getTechniques(sectionId: number): Observable<any> {
    return this.apiService.get<any>(
      `api/v1/programs/assessments/teacher/techniques/section/${sectionId}`
    );
  }
}
